import { Injectable } from '@angular/core';
import { BillFile, BillStatus, ParsedBillData, CustomerProfile, OnboardingContext } from '../models';
import { CommodityType, parseCommodityTypes } from '../../zentility/_shared/_zen-legacy-common/_models/commodity';

const STORAGE_KEY_PREFIX = 'ai_ob_';
const SESSION_EXPIRY_DAYS = 7;

interface SerializedBillFile {
  id: string;
  fileName: string;
  fileType: string;
  fileSize: number;
  // fileDataUrl removed, storing File in IndexedDB
  hasIdbFile?: boolean;
  status: BillStatus;
  data: ParsedBillData | null;
  error?: string;
  duplicateOfId?: string;
  tempFileId?: string;
  billAiId?: number;
  warnings?: string[];
  commodityTypes?: CommodityType[];
  commodityType?: CommodityType;
  dualFuelOf?: string;
}

export interface StoredSession {
  context: OnboardingContext;
  bills: SerializedBillFile[];
  manualProfile: Partial<CustomerProfile> | null;
  isBudgetingMode: boolean;
  /** Pairing keys the user rejected, so unpairs survive a refresh. */
  rejectedPairings?: string[];
  createdAt: number;
  lastModifiedAt: number;
  expiresAt: number;
}

export interface SessionSummary {
  storageKey: string;
  context: OnboardingContext;
  billCount: number;
  createdAt: number;
  lastModifiedAt: number;
  expiresAt: number;
}

@Injectable({
  providedIn: 'root'
})
export class StorageService {

  /**
   * Gets the full localStorage key for a context
   */
  private getFullKey(storageKey: string): string {
    return `${STORAGE_KEY_PREFIX}${storageKey}`;
  }

  /**
   * Saves a session for a specific context
   */
  async saveSession(
    context: OnboardingContext,
    bills: BillFile[],
    manualProfile: Partial<CustomerProfile> | null,
    isBudgetingMode: boolean,
    rejectedPairings: string[] = []
  ): Promise<void> {
    try {
      const serializedBills: SerializedBillFile[] = await Promise.all(
        bills.map(bill => this.serializeBill(bill))
      );

      const now = Date.now();
      const existingSession = this.getSessionRaw(context.storageKey);
      const createdAt = existingSession?.createdAt ?? now;

      const session: StoredSession = {
        context,
        bills: serializedBills,
        manualProfile,
        isBudgetingMode,
        rejectedPairings,
        createdAt,
        lastModifiedAt: now,
        expiresAt: now + (SESSION_EXPIRY_DAYS * 24 * 60 * 60 * 1000)
      };

      const fullKey = this.getFullKey(context.storageKey);
      localStorage.setItem(fullKey, JSON.stringify(session));
    } catch (error) {
      console.error('Error saving AI onboarding session:', error);
    }
  }

  /**
   * Loads a session for a specific context
   */
  async loadSession(storageKey: string): Promise<{
    context: OnboardingContext;
    bills: BillFile[];
    manualProfile: Partial<CustomerProfile> | null;
    isBudgetingMode: boolean;
    rejectedPairings: string[];
  } | null> {
    try {
      const session = this.getSessionRaw(storageKey);
      if (!session) return null;

      // Check if expired
      if (this.isSessionExpired(session)) {
        this.clearSession(storageKey);
        return null;
      }

      const bills: BillFile[] = await Promise.all(
        session.bills.map(serialized => this.deserializeBill(serialized))
      );

      return {
        context: session.context,
        bills,
        manualProfile: session.manualProfile,
        isBudgetingMode: session.isBudgetingMode,
        rejectedPairings: session.rejectedPairings || []
      };
    } catch (error) {
      console.error('Error loading AI onboarding session:', error);
      return null;
    }
  }

  /**
   * Gets raw session data without deserializing files
   */
  private getSessionRaw(storageKey: string): StoredSession | null {
    try {
      const fullKey = this.getFullKey(storageKey);
      const stored = localStorage.getItem(fullKey);
      if (!stored) return null;
      return JSON.parse(stored) as StoredSession;
    } catch (error) {
      console.warn('Failed to parse stored session:', error);
      return null;
    }
  }

  /**
   * Checks if a session is expired
   */
  private isSessionExpired(session: StoredSession): boolean {
    return Date.now() > session.expiresAt;
  }

  /**
   * Checks if a session exists for a specific context
   */
  hasSession(storageKey: string): boolean {
    const session = this.getSessionRaw(storageKey);
    if (!session) return false;
    if (this.isSessionExpired(session)) {
      this.clearSession(storageKey);
      return false;
    }
    return session.bills.length > 0;
  }

  /**
   * Checks whether a (non-expired) session record exists for a key, regardless
   * of whether it holds any bills. Use this to verify a write persisted;
   * use hasSession() to decide whether there's a resumable session worth showing.
   */
  hasStoredSession(storageKey: string): boolean {
    const session = this.getSessionRaw(storageKey);
    if (!session) return false;
    if (this.isSessionExpired(session)) {
      this.clearSession(storageKey);
      return false;
    }
    return true;
  }

  /**
   * Clears a specific session from storage.
   * NOTE: Does NOT auto-clear bill AI flow state — call clearBillAiFlowState() explicitly
   * when the user completes or intentionally discards the flow.
   */
  clearSession(storageKey: string): void {
    const fullKey = this.getFullKey(storageKey);
    const session = this.getSessionRaw(storageKey);
    if (session && session.bills) {
      // Fire and forget deleting files from IDB
      session.bills.forEach(bill => {
        if (bill.id) {
          this.deleteFileFromIdb(bill.id).catch(e => console.warn('Failed to delete file from IDB', e));
        }
      });
    }
    localStorage.removeItem(fullKey);
  }

  /**
   * Gets summary for a single session by key (direct lookup, no full-scan).
   */
  getSessionSummary(storageKey: string): SessionSummary | null {
    const session = this.getSessionRaw(storageKey);
    if (!session) return null;
    if (this.isSessionExpired(session)) {
      this.clearSession(storageKey);
      return null;
    }
    return {
      storageKey,
      context: session.context,
      billCount: session.bills.length,
      createdAt: session.createdAt,
      lastModifiedAt: session.lastModifiedAt,
      expiresAt: session.expiresAt
    };
  }

  /**
   * Gets all active (non-expired) sessions
   */
  getAllSessions(): SessionSummary[] {
    const sessions: SessionSummary[] = [];
    const keysToRemove: string[] = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key?.startsWith(STORAGE_KEY_PREFIX)) continue;

      try {
        const stored = localStorage.getItem(key);
        if (!stored) continue;

        const session: StoredSession = JSON.parse(stored);

        // Check expiry
        if (this.isSessionExpired(session)) {
          keysToRemove.push(key);
          continue;
        }

        const storageKey = key.replace(STORAGE_KEY_PREFIX, '');
        sessions.push({
          storageKey,
          context: session.context,
          billCount: session.bills.length,
          createdAt: session.createdAt,
          lastModifiedAt: session.lastModifiedAt,
          expiresAt: session.expiresAt
        });
      } catch (error) {
        console.warn('Invalid session, removing:', key, error);
        keysToRemove.push(key);
      }
    }

    // Clean up expired/invalid sessions
    keysToRemove.forEach(key => localStorage.removeItem(key));

    return sessions;
  }

  /**
   * Gets count of active sessions
   */
  getSessionCount(): number {
    return this.getAllSessions().length;
  }

  /**
   * Cleans up all expired sessions
   * Call this on app/page load
   */
  cleanupExpiredSessions(): number {
    let cleaned = 0;

    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (!key?.startsWith(STORAGE_KEY_PREFIX)) continue;

      try {
        const stored = localStorage.getItem(key);
        if (!stored) continue;

        const session: StoredSession = JSON.parse(stored);
        if (this.isSessionExpired(session)) {
          localStorage.removeItem(key);
          cleaned++;
        }
      } catch (error) {
        console.warn('Invalid session during cleanup, removing:', key, error);
        localStorage.removeItem(key);
        cleaned++;
      }
    }

    return cleaned;
  }

  /**
   * Clears all onboarding sessions
   */
  clearAllSessions(): void {
    const keysToRemove: string[] = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(STORAGE_KEY_PREFIX)) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach(key => localStorage.removeItem(key));
  }

  // ============================================================================
  // Legacy support - for portfolio-level onboarding (backwards compatibility)
  // ============================================================================

  /**
   * @deprecated Use saveSession with context instead
   * Saves portfolio-level session (legacy method)
   */
  async savePortfolioSession(
    bills: BillFile[],
    manualProfile: Partial<CustomerProfile> | null,
    isBudgetingMode: boolean
  ): Promise<void> {
    const context: OnboardingContext = {
      level: 'PORTFOLIO',
      storageKey: 'portfolio'
    };
    await this.saveSession(context, bills, manualProfile, isBudgetingMode);
  }

  /**
   * @deprecated Use loadSession('portfolio') instead
   * Loads portfolio-level session (legacy method)
   */
  async loadPortfolioSession(): Promise<{
    bills: BillFile[];
    manualProfile: Partial<CustomerProfile> | null;
    isBudgetingMode: boolean;
  } | null> {
    const result = await this.loadSession('portfolio');
    if (!result) return null;
    return {
      bills: result.bills,
      manualProfile: result.manualProfile,
      isBudgetingMode: result.isBudgetingMode
    };
  }

  /**
   * @deprecated Use hasSession('portfolio') instead
   */
  hasPortfolioSession(): boolean {
    return this.hasSession('portfolio');
  }

  /**
   * @deprecated Use clearSession('portfolio') instead
   */
  clearPortfolioSession(): void {
    this.clearSession('portfolio');
  }

  // ============================================================================
  // Bill AI Flow State Persistence
  // ============================================================================

  /**
   * Saves bill-AI-flow-specific state (step progress, selections, edits).
   * Stored separately from bill data to avoid re-serializing files on every change.
   */
  saveBillAiFlowState(storageKey: string, billAiFlowState: any): void {
    try {
      const fullKey = `${STORAGE_KEY_PREFIX}${storageKey}_billAiFlow`;
      localStorage.setItem(fullKey, JSON.stringify({
        ...billAiFlowState,
        savedAt: Date.now()
      }));
    } catch (error) {
      console.error('Error saving bill AI flow state:', error);
    }
  }

  /**
   * Loads bill AI flow state for a given storage key.
   * Returns null if no state found or if main session doesn't exist.
   */
  loadBillAiFlowState(storageKey: string): any | null {
    try {
      // Only restore if the main session still exists
      if (!this.hasSession(storageKey)) return null;

      const fullKey = `${STORAGE_KEY_PREFIX}${storageKey}_billAiFlow`;
      const stored = localStorage.getItem(fullKey);
      if (!stored) return null;
      return JSON.parse(stored);
    } catch (error) {
      console.warn('Failed to load bill AI flow state:', error);
      return null;
    }
  }

  /**
   * Checks if bill AI flow state exists for a given storage key.
   */
  hasBillAiFlowState(storageKey: string): boolean {
    const fullKey = `${STORAGE_KEY_PREFIX}${storageKey}_billAiFlow`;
    return localStorage.getItem(fullKey) !== null;
  }

  /**
   * Clears bill AI flow state for a given storage key.
   */
  clearBillAiFlowState(storageKey: string): void {
    const fullKey = `${STORAGE_KEY_PREFIX}${storageKey}_billAiFlow`;
    localStorage.removeItem(fullKey);
  }

  
  // ============================================================================
  // IndexedDB Helpers for Large Files
  // ============================================================================

  private getDb(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const req = indexedDB.open('AiOnboardingDB', 1);
      req.onupgradeneeded = () => {
        if (!req.result.objectStoreNames.contains('files')) {
          req.result.createObjectStore('files');
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  }

  private async saveFileToIdb(id: string, file: File): Promise<void> {
    const db = await this.getDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction('files', 'readwrite');
      const store = tx.objectStore('files');
      const req = store.put(file, id);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  }

  private async getFileFromIdb(id: string): Promise<File | null> {
    const db = await this.getDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction('files', 'readonly');
      const store = tx.objectStore('files');
      const req = store.get(id);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => reject(req.error);
    });
  }

  private async deleteFileFromIdb(id: string): Promise<void> {
    const db = await this.getDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction('files', 'readwrite');
      const store = tx.objectStore('files');
      const req = store.delete(id);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  }


  // ============================================================================
  // Serialization helpers
  // ============================================================================

  /**
   * Converts a BillFile to a serializable format
   */
  private async serializeBill(bill: BillFile): Promise<SerializedBillFile> {
    let hasIdbFile = false;
    try {
      if (bill.file) {
        await this.saveFileToIdb(bill.id, bill.file);
        hasIdbFile = true;
      }
    } catch (e) {
      console.warn('Failed to save file to IDB for bill', bill.id, e);
    }

    return {
      id: bill.id,
      fileName: bill.file?.name || 'unknown.pdf',
      fileType: bill.file?.type || 'application/pdf',
      fileSize: bill.file?.size || 0,
      hasIdbFile,
      status: bill.status,
      data: bill.data,
      error: bill.error,
      duplicateOfId: bill.duplicateOfId,
      tempFileId: bill.tempFileId,
      billAiId: bill.billAiId,
      warnings: bill.warnings,
      commodityTypes: bill.commodityTypes,
      commodityType: bill.commodityType,
      dualFuelOf: bill.dualFuelOf,
    };
  }

  /**
   * Converts a serialized bill back to a BillFile
   */
  private async deserializeBill(serialized: SerializedBillFile): Promise<BillFile> {
    let file: File | null = null;
    let fileData: Uint8Array | undefined;
    let previewUrl = '';

    if (serialized.hasIdbFile) {
      try {
        file = await this.getFileFromIdb(serialized.id);
      } catch (e) {
        console.warn('Failed to load file from IDB for bill', serialized.id, e);
      }
    }

    if (!file) {
      // IDB failed or was cleared — create a placeholder File so bill metadata survives.
      // fileData stays undefined to signal no renderable PDF content.
      const blob = new Blob([], { type: serialized.fileType });
      file = new File([blob], serialized.fileName, { type: serialized.fileType });
    } else {
      try {
        const buffer = await file.arrayBuffer();
        if (buffer.byteLength > 0) {
          fileData = new Uint8Array(buffer);
        }
        // Regenerate blob URL so the PDF viewer has a usable source
        previewUrl = URL.createObjectURL(file);
      } catch (e) {
        console.warn('Failed to read arrayBuffer', e);
      }
    }

    return {
      id: serialized.id,
      file,
      previewUrl,
      fileData,
      status: serialized.status,
      data: serialized.data,
      error: serialized.error,
      duplicateOfId: serialized.duplicateOfId,
      tempFileId: serialized.tempFileId,
      billAiId: serialized.billAiId,
      warnings: serialized.warnings,
      commodityTypes: parseCommodityTypes(serialized.commodityTypes),
      commodityType: serialized.commodityType,
      dualFuelOf: serialized.dualFuelOf,
    };
  }

  /**
   * Converts a File to a data URL (base64)
   */
  private fileToDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  /**
   * Converts a data URL back to a File
   */
  private async dataUrlToFile(
    dataUrl: string,
    fileName: string,
    fileType: string
  ): Promise<File> {
    const response = await fetch(dataUrl);
    const blob = await response.blob();
    return new File([blob], fileName, { type: fileType });
  }
}
